import { RefreshRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import { API_BASE } from "../../config/api";
import { getCustomers } from "../../services/crm.service";
import { getAdminOrders, getAdminProducts } from "../../services/order.service";
import { getSupportTasks } from "../../services/task.service";

type IntegrationStatus = {
  name: string;
  endpoint: string;
  state: "ok" | "error";
  detail: string;
};

export default function CrmIntegrations() {
  const [items, setItems] = useState<IntegrationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [ordersRes, customersRes, productsRes, supportRes] =
        await Promise.allSettled([
          getAdminOrders({ page: 1, page_size: 1 }),
          getCustomers({ page: 0, page_size: 1 }),
          getAdminProducts({ page: 1, page_size: 1 }),
          getSupportTasks("pending"),
        ]);

      const nextItems: IntegrationStatus[] = [
        {
          name: "Order Service",
          endpoint: API_BASE.ORDER_ADMIN,
          state: ordersRes.status === "fulfilled" ? "ok" : "error",
          detail:
            ordersRes.status === "fulfilled"
              ? `Connected (${ordersRes.value.total_items || 0} total orders)`
              : "Request failed",
        },
        {
          name: "Customer Service",
          endpoint: API_BASE.CUSTOMER_ADMIN,
          state: customersRes.status === "fulfilled" ? "ok" : "error",
          detail:
            customersRes.status === "fulfilled"
              ? `Connected (${customersRes.value.total_records || 0} total customers)`
              : "Request failed",
        },
        {
          name: "Product Inventory",
          endpoint: `${API_BASE.ORDER_ADMIN}/products`,
          state: productsRes.status === "fulfilled" ? "ok" : "error",
          detail:
            productsRes.status === "fulfilled"
              ? `Connected (${productsRes.value.total_items || 0} total products)`
              : "Request failed",
        },
        {
          name: "Support Tasks",
          endpoint: "tasks/support/pending",
          state: supportRes.status === "fulfilled" ? "ok" : "error",
          detail:
            supportRes.status === "fulfilled"
              ? `Connected (${supportRes.value.length} pending tasks)`
              : "Request failed",
        },
      ];

      setItems(nextItems);
    } catch (fetchError) {
      console.error(fetchError);
      setError("Could not load integration status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Box>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography variant="h5">Integrations</Typography>
            <Typography variant="body2" color="text.secondary">
              Live status for CRM dependencies.
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshRounded />}
            variant="outlined"
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {loading
            ? [...Array(4)].map((_, index) => (
                <Grid item xs={12} md={6} key={`loading-${index}`}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Skeleton height={22} width="40%" />
                    <Skeleton height={20} width="90%" />
                    <Skeleton height={20} width="60%" />
                  </Paper>
                </Grid>
              ))
            : items.map((item) => (
                <Grid item xs={12} md={6} key={item.name}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderColor:
                        item.state === "ok" ? "success.light" : "error.light",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", wordBreak: "break-all", mt: 0.5 }}
                    >
                      {item.endpoint}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        item.state === "ok" ? "success.main" : "error.main"
                      }
                      sx={{ mt: 1 }}
                    >
                      {item.detail}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
        </Grid>
      </Paper>
    </Box>
  );
}
